import API_CONSTANT from '../constants/APIConstant';
import ROUTER_CONSTANT from '../constants/RouterConstant';
import { GENERIC_ERROR_MESSAGE } from '../constants/ErrorMessages';
import { API_TIMEOUT_MS } from '../constants/config';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { signout } from '../container/login';
import { NavigateFunction } from 'react-router-dom';

export interface ApiResult<T> {
  success: boolean;
  data?: T;
  message?: string;
  status?: number;
}

// Dispatched app-wide whenever the backend responds 402 with code
// SUBSCRIPTION_EXPIRED — see GlobalSubscriptionExpiredListener, which shows a
// single debounced toast for it regardless of which API call triggered it.
export const SUBSCRIPTION_EXPIRED_EVENT = 'subscription-expired';

// Dispatched when the backend responds 403 with code FEATURE_NOT_IN_PLAN — the society's
// plan doesn't include the feature behind this API. Same listener, same debounced toast.
export const FEATURE_NOT_IN_PLAN_EVENT = 'feature-not-in-plan';

const withTimeout = (ms: number, upstreamSignal?: AbortSignal | null) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  if (upstreamSignal) {
    upstreamSignal.addEventListener('abort', () => controller.abort());
  }
  const cleanup = () => clearTimeout(id);
  return { signal: controller.signal, cleanup };
};

async function request<T>(
  navigate: NavigateFunction,
  path: string,
  init: RequestInit = {},
  base = API_CONSTANT.ENDPOINTS.IHOME,
  timeoutMs = API_TIMEOUT_MS
): Promise<ApiResult<T>> {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      navigate(ROUTER_CONSTANT.ROOT.LOGIN);
      return { success: false, message: 'Not authenticated' };
    }

    const headers: HeadersInit = {
      ...(init.headers || {}),
      // keep existing backend header naming
      token,
    } as HeadersInit;

    // Only set content-type for requests with body if not already present
    if (init.body && !(headers as any)['Content-Type']) {
      (headers as any)['Content-Type'] = 'application/json';
    }

    const { signal, cleanup } = withTimeout(timeoutMs, (init as any).signal ?? undefined);
    const res = await fetch(base + path, { ...init, headers, signal });
    cleanup();

    const status = res.status;
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json().catch(() => undefined) : undefined;

    if (status === 401) {
      signout(navigate);
      return { success: false, status, message: 'Session expired' };
    }

    if (status === 402 && payload?.code === 'SUBSCRIPTION_EXPIRED') {
      window.dispatchEvent(new CustomEvent(SUBSCRIPTION_EXPIRED_EVENT, { detail: payload.message }));
      return { success: false, status, message: payload.message };
    }

    if (status === 403 && payload?.code === 'FEATURE_NOT_IN_PLAN') {
      // Background reads (e.g. the home feed's maintenance card) fail quietly; gated pages are
      // already covered by FeatureRoute, so only actions get the toast.
      if ((init.method ?? 'GET').toUpperCase() !== 'GET') {
        window.dispatchEvent(new CustomEvent(FEATURE_NOT_IN_PLAN_EVENT, { detail: payload.message }));
      }
      return { success: false, status, message: payload.message };
    }

    if (!res.ok) {
      // payload?.message is the backend's own curated, user-facing text — pass it
      // through as-is. res.statusText ("Internal Server Error", "Bad Gateway", ...)
      // is not something a resident should ever see, so it's never the fallback.
      return { success: false, status, message: payload?.message || GENERIC_ERROR_MESSAGE };
    }

    return { success: true, status, data: payload as T };
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      return { success: false, message: 'Request timed out' };
    }
    console.error('API error:', error);
    return { success: false, message: 'Network error' };
  }
}

export const get = <T>(navigate: NavigateFunction, path: string, init?: RequestInit) =>
  request<T>(navigate, path, { method: 'GET', ...(init || {}) });

export const postJson = <T>(navigate: NavigateFunction, path: string, body: unknown, init?: RequestInit) =>
  request<T>(navigate, path, { method: 'POST', body: JSON.stringify(body), ...(init || {}) });

export const putJson = <T>(navigate: NavigateFunction, path: string, body: unknown, init?: RequestInit) =>
  request<T>(navigate, path, { method: 'PUT', body: JSON.stringify(body), ...(init || {}) });

export const deleteJson = <T>(navigate: NavigateFunction, path: string, body?: unknown, init?: RequestInit) =>
  request<T>(navigate, path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined, ...(init || {}) });


